import type { IAppleOffer } from '@/lib/api/apple-billing.client';
import { usesWebBilling } from '@/lib/native/purchase-visibility';
import { isStoreKitAvailable } from '@/lib/native/storekit';
import { useAppStoreOfferStore } from '@/lib/stores/app-store-offer.store';
import { useSessionStore } from '@/lib/stores/session.store';

/**
 * Whether the App-Store screen has anything to show: a purchase may start, or
 * there is a subscription to look at, restore and manage. Without either it
 * stays closed — an empty purchase screen is what App Review rejects.
 *
 * The feature flag reaches this through the server: with it off the offer is
 * never eligible, but a subscription bought earlier still opens the screen.
 */
function appStoreScreenOpen(offer: IAppleOffer | null): boolean {
  return !!offer && (offer.eligible || !!offer.subscription);
}

/**
 * Whether /settings/billing opens (KAN-10):
 *
 * - on the web always, as the Stripe billing page, and so for super-admins in
 *   the apps (see usesWebBilling);
 * - in the iOS app as the App-Store screen, once its offer is loaded and has
 *   something (AppStoreSession loads it for every signed-in workspace);
 * - in the Android app and in iOS shells without the StoreKit plugin never.
 *
 * Read at call time, for the route guard.
 */
export function billingPageOpens(): boolean {
  if (usesWebBilling(useSessionStore.getState().profile)) return true;
  return isStoreKitAvailable() && appStoreScreenOpen(useAppStoreOfferStore.getState().offer);
}

/** {@link billingPageOpens} for the settings entry, re-rendering once the offer is in. */
export function useBillingPageOpens(): boolean {
  const offer = useAppStoreOfferStore((state) => state.offer);
  const profile = useSessionStore((state) => state.profile);
  if (usesWebBilling(profile)) return true;
  return isStoreKitAvailable() && appStoreScreenOpen(offer);
}

/** Inside the iOS app: whether this workspace may buy a licence through the App Store now. */
export function useAppStorePurchaseOpen(): boolean {
  const eligible = useAppStoreOfferStore((state) => state.offer?.eligible === true);
  return eligible && isStoreKitAvailable();
}
